/**
 * "무엇에다" 저장하는지 알려주기 위한 이름 조회
 *
 * 왜 필요한가:
 *   확인 카드에는 @판매번호 12345 처럼 번호만 뜬다. 번호를 외우고 다니는 사람은 없으므로
 *   그 화면에서 사용자가 실제로 검증할 수 있는 건 "저장한다"는 사실뿐이고, 대상이 맞는지는
 *   확인할 방법이 없다. AI 가 예약을 잘못 짚어도 그대로 통과한다.
 *
 *   그래서 번호 옆에 이름을 같이 보여준다. 이름은 AI 에게 물어보지 않는다 —
 *   AI 가 지어내면 틀린 대상으로 안심시키는 꼴이라 없느니만 못하다. 서버에서 받아온다.
 *
 * 세 종류를 다룬다:
 *   상품(@상품번호) — 목록(TRP_S_투어상품리스트)을 통째로 받아 캐시한다. 파라미터가 업체번호뿐이라
 *                     한 번이면 되고, 이후 번호를 바꿔 가며 확인해도 서버를 다시 부르지 않는다.
 *   예약(@판매번호) — 단건 조회다. 판매정보에서 상품명·출발일을, 거기 딸린 주문번호로 주문정보에서
 *                     예약자를 얻는다. 예약을 특정하는 데는 "누가, 어떤 상품을, 언제" 가 가장 빠르다.
 *   주문(@주문번호) — 주문정보 한 번으로 주문자와 상품명이 나온다.
 *
 * 캐시는 업체번호로 묶는다. 로그아웃 후 다른 회사로 로그인하면 앞 회사 이름이 그대로 남는데,
 * 그러면 "확인시켜 주는 이름" 이 남의 회사 것이 되어 정반대 효과가 난다.
 */

import { getAuthHeader, getUserInfo } from '../../utils/tokenManager';
import { API_URL } from '../../config/apiConfig';

/** { companyNo, products: Map|null, sales: Map, orders: Map } */
let cache = null;
/** 같은 번호를 동시에 여러 번 물어보지 않게 잡아둔다. key → Promise */
const inflight = new Map();

const currentCompanyNo = () => String((getUserInfo() || {}).companyNo ?? '');

/** 지금 로그인한 업체의 캐시. 업체가 바뀌었으면 새로 만든다. */
function store() {
  const companyNo = currentCompanyNo();
  if (!cache || cache.companyNo !== companyNo) {
    cache = { companyNo, products: null, sales: new Map(), orders: new Map() };
    inflight.clear();
  }
  return cache;
}

/** 캐시를 비운다 (테스트에서 쓴다) */
export function resetTargetCache() {
  cache = null;
  inflight.clear();
}

// ── 서버 호출 ────────────────────────────────────────────────────

/** SP 하나를 부르고 첫 결과 테이블을 돌려준다. 자동 채움 값은 여기서 넣는다. */
async function callSp(procedureName, params) {
  const { companyNo, userNo } = getUserInfo() || {};
  const table1 = params.map(({ name, value, auto }) => ({
    컬럼명: name,
    값: auto === 'companyNo' ? String(companyNo ?? '') : auto === 'userNo' ? String(userNo ?? '') : value,
  }));

  const response = await fetch(`${API_URL}?procedureName=${encodeURIComponent(procedureName)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ Table1: table1 }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const data = await response.json();
  const rows = data?.table || data?.table1 || data?.Table || data?.Table1 || [];
  return Array.isArray(rows) ? rows : [];
}

/** 여러 후보 컬럼명 중 값이 있는 첫 번째를 고른다. 서버마다 컬럼명이 조금씩 달라서 방어적으로 읽는다. */
function pick(row, ...names) {
  for (const n of names) {
    const v = row?.[n];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** '2026-09-12T00:00:00' 같은 값을 날짜만 남긴다 */
const dateOnly = (v) => (/^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : v);

async function loadProducts() {
  const rows = await callSp('DBO.TRP_S_투어상품리스트', [{ name: '@공급자번호', auto: 'companyNo' }]);
  const map = new Map();
  for (const row of rows) {
    const no = pick(row, '상품번호');
    const name = pick(row, '상품명', '판매상품명');
    if (no && name) map.set(no, name);
  }
  return map;
}

async function loadOrder(orderNo) {
  const rows = await callSp('DBO.TRP_S_주문조회_주문정보', [
    { name: '@업체번호', auto: 'companyNo' },
    { name: '@유저번호', auto: 'userNo' },
    { name: '@주문번호', value: String(orderNo) },
  ]);
  const row = rows[0];
  if (!row) return null;
  return {
    주문자: pick(row, '주문자', '예약자', '주문자명'),
    상품명: pick(row, '판매상품명', '상품명'),
  };
}

async function loadSale(saleNo) {
  const rows = await callSp('DBO.TRP_S_예약관리_판매정보', [
    { name: '@공급업체번호', auto: 'companyNo' },
    { name: '@유저번호', auto: 'userNo' },
    { name: '@판매번호', value: Number(saleNo) || 0 },
  ]);
  const row = rows[0];
  if (!row) return null;

  const info = {
    상품명: pick(row, '상품명', '판매상품명'),
    출발일: dateOnly(pick(row, '출발일', '행사시작일')),
    인원: pick(row, '총인원'),
    예약자: pick(row, '예약자', '주문자'), // 서버가 같이 주면 주문정보를 또 부르지 않는다
  };

  // 판매정보에 예약자가 없으면 거기 딸린 주문번호로 한 번 더 본다.
  // 예약을 알아보는 데 사람 이름만 한 게 없어서 한 번의 왕복은 감수한다.
  if (!info.예약자) {
    const orderNo = pick(row, '주문번호', '주문판매번호');
    if (orderNo) {
      try {
        const order = await loadOrder(orderNo);
        if (order?.주문자) info.예약자 = order.주문자;
        if (!info.상품명 && order?.상품명) info.상품명 = order.상품명;
      } catch (_) { /* 이름 하나 못 붙이는 것뿐이다 */ }
    }
  }
  return info;
}

// ── 대상 판별 ────────────────────────────────────────────────────

/** 파라미터 이름 → 대상 종류. 위에 있는 것이 우선한다(예약 저장이 상품보다 구체적이다). */
const KEY_RULES = [
  { 종류: '예약', names: ['@판매번호', '판매번호'] },
  { 종류: '주문', names: ['@주문번호', '주문번호'] },
  { 종류: '상품', names: ['@상품번호', '상품번호'] },
];

/**
 * 이 미리보기가 "무엇을" 건드리는지 뽑아낸다.
 * 실제로 서버에 나갈 파라미터에서 읽으므로, AI 가 뭐라고 했든 진짜 대상이 나온다.
 * 화면 도구는 파라미터에 번호가 없을 수 있어 화면 state 를 먼저 본다.
 */
export function targetKeyOf(preview, screenState = null) {
  for (const rule of KEY_RULES) {
    for (const name of rule.names) {
      const raw = screenState?.[name.replace('@', '')];
      const no = String(raw ?? '').trim();
      // 화면 state 는 "(아직 안 읽힘)" 같은 안내문을 넣기도 한다. 숫자만 받는다.
      if (/^\d+$/.test(no) && no !== '0') return { 종류: rule.종류, 번호: no };
    }
  }
  const params = preview?.params;
  if (!Array.isArray(params)) return null;
  for (const rule of KEY_RULES) {
    const hit = params.find((p) => rule.names.includes(p.name));
    const no = String(hit?.value ?? '').trim();
    if (/^\d+$/.test(no) && no !== '0') return { 종류: rule.종류, 번호: no };
  }
  return null;
}

/** 캐시에 있는 것으로 이름표를 만든다. 없으면 이름 없이 번호만. */
function labelFromCache(key) {
  if (!key) return null;
  const s = store();

  if (key.종류 === '상품') {
    return { 종류: '상품', 번호: key.번호, 이름: s.products?.get(key.번호) || '', 부가: '' };
  }
  if (key.종류 === '예약') {
    const info = s.sales.get(key.번호);
    const 부가 = info
      ? [info.예약자 && `예약자 ${info.예약자}`, info.출발일 && `출발 ${info.출발일}`, info.인원 && `${info.인원}명`]
        .filter(Boolean).join(' · ')
      : '';
    return { 종류: '예약', 번호: key.번호, 이름: info?.상품명 || '', 부가 };
  }
  const info = s.orders.get(key.번호);
  return {
    종류: '주문',
    번호: key.번호,
    이름: info?.상품명 || '',
    부가: info?.주문자 ? `주문자 ${info.주문자}` : '',
  };
}

/** 같은 대상을 동시에 여러 번 물어보지 않게 한 번만 실행한다 */
function once(cacheKey, run) {
  if (inflight.has(cacheKey)) return inflight.get(cacheKey);
  const p = run().finally(() => inflight.delete(cacheKey));
  inflight.set(cacheKey, p);
  return p;
}

/**
 * 대상 이름을 서버에서 받아 캐시에 채운다.
 * 실패해도 조용히 넘어간다 — 이름을 못 붙이는 것 때문에 저장이 막히면 안 된다.
 */
export async function preloadTarget(key) {
  if (!key) return;
  const s = store();
  const cacheKey = `${s.companyNo}:${key.종류}:${key.번호}`;

  try {
    if (key.종류 === '상품') {
      if (s.products) return;
      await once(cacheKey, async () => { s.products = await loadProducts(); });
    } else if (key.종류 === '예약') {
      if (s.sales.has(key.번호)) return;
      await once(cacheKey, async () => { s.sales.set(key.번호, await loadSale(key.번호)); });
    } else {
      if (s.orders.has(key.번호)) return;
      await once(cacheKey, async () => { s.orders.set(key.번호, await loadOrder(key.번호)); });
    }
  } catch (error) {
    try { console.warn('[targetInfo] 대상 이름을 받아오지 못했습니다', error); } catch (_) {}
    // 매번 기다리지 않도록 "없음" 으로 확정한다. 이름 없이 번호만 뜬다.
    if (key.종류 === '상품') s.products = s.products || new Map();
    else if (key.종류 === '예약') s.sales.set(key.번호, null);
    else s.orders.set(key.번호, null);
  }
}

/** 미리보기에 대상 정보를 얹는다 (캐시에서 동기로). 붙일 게 없으면 원본 그대로. */
export function attachTarget(preview, screenState = null) {
  if (!preview) return preview;
  const target = labelFromCache(targetKeyOf(preview, screenState));
  return target ? { ...preview, target } : preview;
}

/**
 * 받아온 뒤 이름표를 붙인다. 확인 창이 값이 바뀔 때마다 부른다.
 * (rebuild 는 동기라 그 안에서는 못 부른다. 그래서 창이 따로 이걸 부른다.)
 */
export async function resolveTarget(preview, screenState = null) {
  const key = targetKeyOf(preview, screenState);
  if (!key) return null;
  await preloadTarget(key);
  return labelFromCache(key);
}
